import React, { useState } from "react";
import { Navigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { userService } from "../../_services";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader2, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  Fname: z.string().min(3, { message: "First Name must be at least 3 characters long" }),
  Lname: z.string().min(2, { message: "Last Name must be at least 2 characters long" }),
  Phone: z.string().min(1, { message: "Phone Number is required" }),
  Email: z.string().min(1, { message: "Enter your email" }).email({ message: "Invalid Email" }),
});

const SignUp_first_step_New = ({ isSecUser, onNext }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [extraInfo, setExtraInfo] = useState(false);
    const [extraInfoMsg, setExtraInfoMsg] = useState('');
    const [emailEV, setEmailEV] = useState(false);

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            Fname: '',
            Lname: '',
            Phone: '',
            Email: '',
        },
    });

    // Check if user is already logged in
    if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/Affiliate_Url" replace />;
    }

    const validateUserData = async (phone, email) => {
        try {
            const apidata = await userService.CheckAvailability(phone, email, '', false);
            
            if (apidata.length > 0) {
                if (apidata[0].code !== "1") {
                    if (apidata[0].message === 'This email and phone no. is already taken') {
                        form.setError('Phone', { message: 'Phone Number already exists' });
                        form.setError('Email', { message: 'This email already exists' });
                        form.setValue('Phone', '');
                        form.setValue('Email', '');
                    }
                    else if (apidata[0].message === 'This phone no. is already taken') {
                        form.setError('Phone', { message: 'Phone Number already exists' });
                        form.setValue('Phone', '');
                    }
                    else if (apidata[0].message === "We've sent an account updation link. Please check your mail inbox/spam.") {
                        setExtraInfo(true);
                        setExtraInfoMsg("This User is already in the system, Setup required. Setuplink mailed to you");
                    }
                    else {
                        if (apidata[0].code === "-2") {
                            setEmailEV(true);
                        } else {
                            setEmailEV(false);
                            form.setValue('Email', '');
                        }
                        form.setError('Email', { message: apidata[0].message });
                    }
                    return false;
                }
                else {
                    // Success - move to next step
                    return true;
                }
            }
            return false;
        }
        catch (error) {
            console.error('User validation error:', error);
            return false;
        }
    };

    const onSubmit = async (values) => {
        setIsLoading(true);
        setExtraInfo(false);
        setExtraInfoMsg('');
        
        try {
            // First validate phone number format
            const phoneApiData = await userService.GetPhoneNoValidate(values.Phone);
            
            if (phoneApiData[0].code !== "1") {
                form.setError('Phone', { message: 'Invalid Phone Number' });
                setIsLoading(false);
                return;
            } else {
                if (phoneApiData[0].message != null) {
                    form.setValue('Phone', phoneApiData[0].message);
                    values.Phone = phoneApiData[0].message;
                }
            }

            // Validate phone number length
            const phoneDigits = values.Phone.replace(/[- )(]/g, '');
            if (phoneDigits.length !== 10) {
                form.setError('Phone', { message: 'Phone Number must be 10 digits' });
                setIsLoading(false);
                return;
            }

            // Validate user data
            const isValid = await validateUserData(values.Phone, values.Email);
            
            if (isValid) {
                // Store signup data and move to next step
                localStorage.setItem('signup_step1', JSON.stringify(values));
                if (onNext) {
                    onNext(values);
                }
            }
        }
        catch (error) {
            console.error('Form submission error:', error);
        }
        finally {
            setIsLoading(false);
        }
    };

    const scrollToContact = () => {
        window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
    };

    return (
        <div className="w-full">
            <h2 className="hidden lg:block text-3xl font-bold text-foreground mb-8">
                Sign Up today & Earn Cash Every Time!
            </h2>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                        control={form.control}
                        name="Fname"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input 
                                            {...field} 
                                            disabled={isLoading}
                                            placeholder=" "
                                            className="peer h-12"
                                            onChange={(e) => {
                                                // Only allow letters
                                                const value = e.target.value;
                                                if (value === '' || /^[a-zA-Z]+$/.test(value)) {
                                                    field.onChange(e);
                                                }
                                            }}
                                        />
                                        <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                            First Name
                                        </FormLabel>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="Lname"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input 
                                            {...field} 
                                            disabled={isLoading}
                                            placeholder=" "
                                            className="peer h-12"
                                            onChange={(e) => {
                                                // Only allow letters
                                                const value = e.target.value;
                                                if (value === '' || /^[a-zA-Z]+$/.test(value)) {
                                                    field.onChange(e);
                                                }
                                            }}
                                        />
                                        <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                            Last Name
                                        </FormLabel>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="Phone"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input 
                                            {...field} 
                                            disabled={isLoading}
                                            placeholder=" "
                                            className="peer h-12"
                                        />
                                        <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                            Phone Number
                                        </FormLabel>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="Email"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <div className="relative">
                                        <Input 
                                            {...field} 
                                            type="email"
                                            disabled={isLoading}
                                            placeholder=" "
                                            className="peer h-12"
                                        />
                                        <FormLabel className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none">
                                            Email
                                        </FormLabel>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {extraInfo && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-blue-800 text-sm">
                            {extraInfoMsg}
                        </div>
                    )}

                    {emailEV && (
                        <Alert variant="warning">
                            <AlertTriangle />
                            <AlertDescription>Please verify your email address</AlertDescription>
                        </Alert>
                    )}
                    <Button type="submit" variant="secondary" size="lg" className="w-auto flex align-center justify-center" disabled={isLoading}>
                        {isLoading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving</> : 'Save & Continue'}
                    </Button>
                </form>
            </Form>

            <div className="mt-6 text-left space-y-2">
                <p className="text-md text-foreground opacity-70">
                    Already have an account?{' '}
                    <a href="/login" className="text-accent hover:no-underline hover:text-foreground font-medium">
                        Sign In
                    </a>
                </p>
                {/*<button 
                    onClick={scrollToContact}
                    className="text-sm text-accent hover:no-underline hover:text-foreground"
                >
                    Have questions? Contact us
                </button>*/}
            </div>
        </div>
    );
};

export default SignUp_first_step_New;
